// Angular Files
import {
 Component, ElementRef, HostBinding, Input, OnInit, Output, EventEmitter, ViewChild, OnDestroy, NgModule, Inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
 animate, state, style, transition, trigger
} from '@angular/animations';
import { Router, RouterModule } from '@angular/router';

// Angular Material Files
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatMenuModule } from '@angular/material/menu';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// Angular CDK Files
import { A11yModule } from '@angular/cdk/a11y';
import {
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    ConnectedPosition,
    OverlayModule
} from '@angular/cdk/overlay';

// Other External Files
import { Subscription } from 'rxjs';

// Teller Online Files
import { ItemPaymentOptionDto } from 'apps/public-portal/src/app/core/api/PublicPortalApiClients';
import {
 ItemService, ItemDetailModel, SearchResult, ItemModel, ItemDetailsModelWithBillUrl, InboundRedirectService, AuthService,
 ItemDetailTypeEnum, PopupDataModel, searchResultDisplayName
} from 'apps/public-portal/src/app/core/services';
import {
    SelectedPaymentOption,
    SelectPaymentComponent,
    SelectPaymentSheetData
} from 'apps/public-portal/src/app/shared/components/select-payment/select-payment.component';
import { SelectPaymentModule } from 'apps/public-portal/src/app/shared/components/select-payment/select-payment.module';

// Teller Online Library files
import { TellerOnlineIconsModule } from 'teller-online-libraries/icons';
import {
 API_BASE_URL, TellerOnlineAppService, TellerOnlineFocusRestorationService, TellerOnlineSiteMetadataService, TellerOnlineHistoryService
} from 'teller-online-libraries/core';
import {
 ColumnContentAlignment, TellerOnlineColumnMetadata, TellerOnlineDialogAction, TellerOnlineDialogComponent, TellerOnlineDialogOptions, TellerOnlineDialogTable, TellerOnlineMessageService, TellerOnlineWindowService
} from 'teller-online-libraries/shared';
import { FlagTypeEnumDto } from 'libraries/core/src/lib/api/CoreWebApi';
import { CartItemDto } from 'apps/admin-portal/src/app/core/api/AdminPortalApiClients';

@Component({
    selector: 'app-search-result',
    templateUrl: './search-result.component.html',
    styleUrls: ['./search-result.component.scss'],
    host: {
        class: 'search-result'
    },
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('300ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false
})
export class SearchResultComponent implements OnInit, OnDestroy {
    @HostBinding('attr.data-item-guid') get itemGuidAttr() {
        return this.item?.itemGuid;
    }

    @Input() item: SearchResult;

    @Input() conflictingOptions = null;

    @Input() public paymentDisabled: boolean = false;

    @Input() public paymentDisabledMessage: string = '';

    @Output() addToCart = new EventEmitter<any>();

    @Output() updateCart = new EventEmitter<any>();

    @Output() removeFromCart = new EventEmitter<any>();

    @Output() loadMore = new EventEmitter<any>();

    @ViewChild(CdkConnectedOverlay) private selectPaymentOverlay?: CdkConnectedOverlay;

    public ItemDetailTypeEnum = ItemDetailTypeEnum;

    /** Unique per instance: several results render on a page, each with its own panel, and the
     * triggers reference theirs via aria-controls. */
    public readonly selectPaymentPanelId = `aria_select_payment_${++SearchResultComponent._panelSeq}`;

    private static _panelSeq = 0;

    /** Whichever trigger opened the panel (Choose Payment Option / Edit / Add to Cart). Their @if
     * conditions are mutually exclusive, so only one can be the origin at a time. */
    public selectPaymentOrigin: CdkOverlayOrigin | null = null;

    /** Beside the trigger first — the panel is tall enough that above/below it covers the item's
     * own details. Only the first entry that fits the viewport whole is used, so the vertical
     * placements remain the fallback on narrower content columns. */
    public readonly selectPaymentPanelPositions: ConnectedPosition[] = [
        {
            originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 8
        },
        {
            originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 8
        },
        {
            originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top'
        },
        {
            originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom'
        }
    ];

    /** Logical open state: drives aria-expanded/aria-controls and flips synchronously on close. */
    public selectPaymentPanelOpen: boolean = false;

    /** Whether the overlay is attached — stays true through the exit animation. */
    public selectPaymentPanelAttached: boolean = false;

    /** Plays the exit animation; its animationend detaches the overlay. */
    public selectPaymentPanelExiting: boolean = false;

    /** Safety net: browsers throttle animations in backgrounded windows, so animationend can be
     * late or absent. Well above the CSS exit duration. */
    private _panelExitFallback: ReturnType<typeof setTimeout> | null = null;

    private static readonly PANEL_EXIT_FALLBACK_MS = 400;

    /** Keeps the repositioning panel clear of the fixed header band. Measured from the live header
     * rather than duplicating $header-height, so it tracks the real chrome. */
    public get overlayViewportMargin(): number {
        return this.el?.nativeElement?.ownerDocument
            ?.querySelector('.header')?.clientHeight ?? 0;
    }

    public itemIsPayable: boolean;

    public itemIsSchedulable: boolean;

    public itemCanSchedule: boolean;

    // The plugin only answers _isSchedulable=false, never why, so this is the scheduling
    // API's own fallback reason (ScheduledPaymentManager) rather than a guess at the cause.
    public readonly scheduleUnavailableMessage = 'This item cannot be scheduled.';

    public collapsed: boolean = false;

    public FlagTypeEnumDto = FlagTypeEnumDto;

    public standardDetails: ItemDetailModel[];

    public actionableDetails: ItemDetailsModelWithBillUrl[];

    private _selectPaymentSheet: MatBottomSheetRef<SelectPaymentComponent> | null = null;

    // Subscriptions
    private _breakpointSubscription: Subscription;

    constructor(
        @Inject(API_BASE_URL) private baseUrl,
        private el: ElementRef,
        private bottomSheet: MatBottomSheet,
        private focusService: TellerOnlineFocusRestorationService,
        private windowService: TellerOnlineWindowService,
        private itemService: ItemService,
        private messageService: TellerOnlineMessageService,
        public appService: TellerOnlineAppService,
        public siteMetadata: TellerOnlineSiteMetadataService,
        public dialog: MatDialog,
        public inboundRedirectService: InboundRedirectService,
        public authService: AuthService,
        private router: Router,
        private historyService: TellerOnlineHistoryService
    ) { }

    ngOnInit(): void {
        // If the item has any negative payment options, it is not payable.
        // Note: If the item has no payment options it is not payable.
        this.itemIsPayable = true;
        this.itemIsSchedulable = false;
        this.itemCanSchedule = false;
        if (this.item.paymentOptions?.some(p => (p.amount == null && p.maxAmount === 0) || p.amount === 0)) {
            this.itemIsPayable = false;
        }
        if (!this.item.paymentOptions?.every(p => p.amount >= 0 || p.maxAmount >= 0)) {
            this.paymentDisabled = true;
            this.paymentDisabledMessage = 'Negative amounts cannot be paid online';
        }
        const schedulableDetail = this.item.details
            ?.find(detail => detail.description === '_isSchedulable');
        if (schedulableDetail) {
            this.itemIsSchedulable = true;
            this.itemCanSchedule = schedulableDetail.value === 'true';
        }

        this._breakpointSubscription = this.appService.breakpoints$.subscribe(() => {
            if (this._isSelectPaymentOpen(!this.appService.isNarrow)) {
                // open the new panel
                this.openSelectPayment();
                // then close the old panel
                this._closeSelectPayment(!this.appService.isNarrow);
            }
        });

        this.standardDetails = this.item.details?.filter(detail => detail.type === ItemDetailTypeEnum.Default);
        this.actionableDetails = this.item.details?.filter(detail => detail.type !== ItemDetailTypeEnum.Default).map(x => {
            let url;
            try {
                const download = JSON.parse(x.value);

                if (download.downloadKey) {
                    url = `${this.windowService.location.origin}/api/public-portal/file-download/{PageId}/{DownloadIdentifier}`;
                    url = url
                        .replace('{PageId}', encodeURIComponent(`${this.item.page!.pageId}`))
                        .replace('{DownloadIdentifier}', encodeURIComponent(`${download.downloadKey}`));
                }
            } catch {
                // Not a downloadable-file detail; leave url unset.
            }

            return new ItemDetailsModelWithBillUrl({
                ...x,
                billUrl: url
            });
        });
    }

    ngOnDestroy() {
        if (this._breakpointSubscription) this._breakpointSubscription.unsubscribe();

        // The overlay goes with the view; make sure a pending exit timer can't fire after that.
        this._detachSelectPaymentPanel();
    }

    onClick_addToCartOrSelectPayment(item, origin?: CdkOverlayOrigin) {
        this.focusService.save('cart', '.search-result');

        // Anchor the panel to whichever trigger was used.
        if (origin) this.selectPaymentOrigin = origin;

        if (!this.checkPaymentOptionNeedsPrompt(item?.paymentOptions)) {
            this.on_addToCart([item, item?.paymentOptions[0]]);
        } else {
            this.openSelectPayment();
        }
    }

    public get addToCartDisabled(): boolean {
        return this.paymentDisabled || this.siteMetadata.parseMenuNotices(this.item.page.pageId);
    }

    onClick_addToCart(item, origin: CdkOverlayOrigin) {
        // A disabledInteractive button still fires click.
        if (this.addToCartDisabled) {
            return;
        }

        this.onClick_addToCartOrSelectPayment(item, origin);
    }

    on_addToCart(e) {
        this.addToCart.emit(e);
    }

    on_updateCartItem(e) {
        this.updateCart.emit(e);
    }

    onClick_removeFromCart(e) {
        this.removeFromCart.emit(e);
    }

    onClick_collapse() {
        this.collapsed = !this.collapsed;
    }

    async onClick_popup(popupJson: string, event: Event) {
        // Captured now: a fetched popup raises the page loader, which makes the page inert and
        // drops focus to the body before the dialog opens and records where to return it.
        const popupButton = event.currentTarget as HTMLElement;
        const dialogData: TellerOnlineDialogOptions = new TellerOnlineDialogOptions();
        let popupResponse: PopupDataModel;
        const popup = JSON.parse(popupJson);

        if (popup.inPopupFormat) {
            popupResponse = new PopupDataModel(JSON.parse(popup.popupData));
        } else {
            this.appService.triggerPageLoading();
            popupResponse = await this.itemService.dataPopup(this.item.page!.pageId, popup.popupKey);
            this.appService.finishPageLoading();
        }

        if (!popupResponse) {
            return;
        }

        dialogData.title = popupResponse.header;
        dialogData.tables = popupResponse.popupDataTables?.map<TellerOnlineDialogTable>(t => ({
            header: t.header,
            rows: t.rows ?? [],
            footer: t.footer,
            hasColumnHeaderRow: t.columnMetadata.some(x => !!x.title),
            columnMetadata: t.columnMetadata.map<TellerOnlineColumnMetadata>(m => ({
                title: m.title,
                contentAlignment: ColumnContentAlignment[m.contentAlignment]
            })) ?? []
        }));
        dialogData.items = popupResponse.details;
        dialogData.content = popupResponse.description;

        dialogData.actions = [
            new TellerOnlineDialogAction({
                text: 'Close',
                close: true
            }),
            new TellerOnlineDialogAction({
                text: 'Print',
                click: () => {
                    this.windowService.print();
                }
            })
        ];

        // TODO : SCCE-57 Make this a full-page-modal for mobile view
        this.dialog.open(TellerOnlineDialogComponent, {
            maxWidth: '100%',
            data: dialogData,
            panelClass: 'dialog--printable',
            restoreFocus: popupButton
        });
    }

    public getPopupSvg(detailJson: string): string {
        return JSON.parse(detailJson)?.svgIcon;
    }

    public viewFileError() {
        this.messageService.notification('Download Unavailable', 'error');
    }

    public checkPaymentOptionNeedsPrompt(paymentOptions: ItemPaymentOptionDto[]): boolean {
        let status = true;

        if (paymentOptions?.length === 1) {
            status = paymentOptions[0].isCustomAmount;
        }

        return status;
    }

    public checkItemAmountIsEditable(item: CartItemDto): boolean {
        if (!this.inboundRedirectService.isInboundRedirectUIContext) {
            return true;
        }

        return item.paymentOptions?.find(opt => opt.isCustomAmount) != null;
    }

    onClick_loadMore() {
        this.loadMore.emit();
    }

    public onClick_schedule(): void {
        // A disabledInteractive button still fires click.
        if (!this.itemCanSchedule) {
            return;
        }

        const scheduleUrl = `/schedule/${this.item.page.pageId}/${this.item.businessSystemItemKey}`;

        if (this.authService.isSignedIn) {
            this.router.navigate([scheduleUrl]);
        } else {
            this.router.navigate(['/sign-in'], {
                state: { redirectUrl: scheduleUrl }
            });
        }
    }

    public get itemHasSinglePaymentOptionSelected() {
        return this.item.amountInCart && ((this.item.selectedPaymentOption && !this.item.selectedPaymentOptions) || this.item.selectedPaymentOptions?.length === 1);
    }

    public getSearchDisplayName(item: ItemModel) {
        return searchResultDisplayName(item);
    }

    /** Options the user has to choose between: supplied by the host page, or derived from the
     * item's own duplicate cart entries when it isn't. Both the panel and the bottom sheet need
     * these, so they are resolved here rather than in the panel's binding alone. */
    public get selectPaymentConflictingOptions(): SelectedPaymentOption[] {
        return this.conflictingOptions
            ?? (this.item.selectedPaymentOptions?.length > 1
                ? this.item.selectedPaymentOptions
                : null);
    }

    /** Close either the panel OR bottom sheet (depending on current width) containing SelectPaymentComponent */
    private _closeSelectPayment(narrow: boolean | null = null) {
        if ((this.appService.isNarrow && narrow === null) || narrow) {
            this.bottomSheet.dismiss();
            this._selectPaymentSheet = null;
        } else if ((this.appService.isWide && narrow === null) || !narrow) {
            this.closeSelectPayment();
        }
    }

    /** Close the wide-viewport payment panel, playing its exit animation. Logical state flips now
     * so aria-expanded doesn't wait on the animation; onAnimationEnd_selectPaymentPanel detaches. */
    public closeSelectPayment() {
        if (!this.selectPaymentPanelOpen) return;

        this.selectPaymentPanelOpen = false;
        this.selectPaymentPanelExiting = true;
        this._panelExitFallback = setTimeout(
            () => this._detachSelectPaymentPanel(),
            SearchResultComponent.PANEL_EXIT_FALLBACK_MS
        );
    }

    /** Escape closes the panel. CDK routes keydown to the top-most overlay only, so a stacked
     * overlay keeps its own Escape. */
    public onKeydown_selectPayment(event: KeyboardEvent) {
        if (event.key !== 'Escape') return;

        event.stopPropagation();
        this.closeSelectPayment();
    }

    /** Detach once the exit animation finishes. The enter animation fires this too, so the exiting
     * flag is what distinguishes them. */
    public onAnimationEnd_selectPaymentPanel() {
        if (!this.selectPaymentPanelExiting) return;

        this._detachSelectPaymentPanel();
    }

    private _detachSelectPaymentPanel() {
        if (this._panelExitFallback) {
            clearTimeout(this._panelExitFallback);
            this._panelExitFallback = null;
        }

        this.selectPaymentPanelExiting = false;
        this.selectPaymentPanelAttached = false;
    }

    /** Open either the mat-menu OR bottom sheet (depending on current width) containing SelectPaymentComponent
     * and await a response from the bottom sheet when on a narrow device.
     */
    public async openSelectPayment() {
        if (this.appService.isNarrow) {
            this._selectPaymentSheet = this.bottomSheet
                .open<SelectPaymentComponent, SelectPaymentSheetData>(SelectPaymentComponent, {
                    panelClass: 'menu-sheet',
                    data: {
                        item: this.item,
                        conflictingOptions: this.selectPaymentConflictingOptions
                    }
                });

            /** Wait for the dismissal of the bottom sheet. This is necessary because we can't bind
             * the outputs of the SelectPaymentComponent as we would normally, but it's set up to pass
             * the relevant data through the afterDismissed event.
             */
            const data = await this._selectPaymentSheet.afterDismissed().toPromise();

            // Determine which type of event it was, if any at all.
            if (data?.paymentOption != null) {
                if (data.action === 'add') this.on_addToCart([data.item, data.paymentOption, data.customAmount]);
                else if (data.action === 'update') this.on_updateCartItem([data.item, data.paymentOption, data.customAmount]);
            }
        } else {
            // Cancel a pending exit and reuse the still-attached panel rather than letting a stale
            // animationend detach it out from under us.
            if (this._panelExitFallback) {
                clearTimeout(this._panelExitFallback);
                this._panelExitFallback = null;
            }

            this.selectPaymentPanelExiting = false;
            this.selectPaymentPanelOpen = true;
            this.selectPaymentPanelAttached = true;
        }
    }

    /** Return the current state of the SelectPaymentComponent */
    private _isSelectPaymentOpen(narrow: boolean | null = null) {
        let open = false;
        if ((this.appService.isNarrow && narrow === null) || narrow) {
            open = this._selectPaymentSheet != null;
        } else if ((this.appService.isWide && narrow === null) || !narrow) {
            open = this.selectPaymentPanelOpen;
        }
        return open;
    }
}

@NgModule({
    declarations: [SearchResultComponent],
    imports: [
        CommonModule,
        RouterModule,
        SelectPaymentModule,
        MatListModule,
        MatMenuModule,
        A11yModule,
        OverlayModule,
        MatIconModule,
        MatTooltipModule,
        MatButtonModule,
        MatDialogModule,
        MatProgressSpinnerModule,
        TellerOnlineIconsModule
    ],
    exports: [SearchResultComponent]

})

export class SearchResultModule { }
